<script setup>
import { isVNode, computed } from 'vue';
import Icon from 'next/icon/Icon.vue';
import Policy from 'dashboard/components/policy.vue';
import { useSidebarContext } from './provider';

const props = defineProps({
  label: { type: String, required: true },
  to: { type: [String, Object], required: true },
  icon: { type: [String, Object], default: null },
  active: { type: Boolean, default: false },
  component: { type: Function, default: null },
  showAlert: { type: Boolean, default: false },
  count: { type: Number, default: 0 },
  // Eltafouk: 'mention' renders a distinct blue "@n" badge so mentions stand
  // out from the red unread counters.
  badgeVariant: { type: String, default: '' },
});

const { resolvePermissions, resolveFeatureFlag } = useSidebarContext();

const shouldRenderComponent = computed(() => {
  return typeof props.component === 'function' || isVNode(props.component);
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <Policy
    :permissions="resolvePermissions(to)"
    :feature-flag="resolveFeatureFlag(to)"
    as="li"
    class="py-0.5 ltr:pl-2 rtl:pr-2 rtl:mr-3 ltr:ml-3 relative text-n-slate-11 child-item before:bg-n-slate-4 after:bg-transparent after:border-n-slate-4 before:left-0 rtl:before:right-0 min-w-0"
  >
    <component
      :is="to ? 'router-link' : 'div'"
      :to="to"
      :title="label"
      class="relative flex h-8 items-center gap-2 px-2 py-1 rounded-lg hover:bg-n-slate-3/60 group min-w-0 transition-colors before:content-[''] before:absolute before:top-1.5 before:bottom-1.5 before:w-[3px] before:rounded-full before:opacity-0 before:bg-n-slate-12 ltr:before:left-0 rtl:before:right-0"
      :class="{
        'text-n-slate-12 bg-n-alpha-2 font-medium active before:opacity-100':
          active,
      }"
    >
      <component
        :is="component"
        v-if="shouldRenderComponent"
        :label
        :icon
        :active
      />
      <template v-else>
        <Icon v-if="icon" :icon="icon" class="size-4 inline-block" />
        <div class="flex-1 truncate min-w-0">{{ label }}</div>
        <span
          v-if="count"
          dir="ltr"
          class="inline-flex flex-shrink-0 items-center justify-center font-semibold leading-none tabular-nums"
          :class="[
            badgeVariant === 'mention'
              ? 'bg-n-blue-9 text-white rounded-full h-[18px] min-w-[18px] px-1.5 text-[10px] gap-px'
              : showAlert
                ? 'bg-[#b91c1c] text-white rounded-full h-[18px] min-w-[18px] px-1 text-[10px] animate-calm-breath'
                : 'bg-n-alpha-2 text-n-slate-11 rounded-md h-5 min-w-5 px-1.5 text-[11px]',
          ]"
        >
          {{ badgeVariant === 'mention' ? `@${count}` : count }}
        </span>
      </template>
    </component>
  </Policy>
</template>

<style scoped>
@keyframes calm-breath {
  0%,
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(185, 28, 28, 0.4);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 4px rgba(185, 28, 28, 0);
  }
}

.animate-calm-breath {
  animation: calm-breath 2.5s ease-in-out infinite;
}
</style>
